import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Ledger ("daftar") primitives — the visual language of the redesigned
 * owner dashboard. Colours stay on the existing token palette; only the
 * typography (Archivo / IBM Plex Mono) and shapes follow the new design.
 */

/* ── License-plate chip ─────────────────────────────────────────── */
export function Plate({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  if (children == null || children === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span
      className={cn(
        "plate-chip inline-block whitespace-nowrap rounded-[5px] border-[1.5px] border-input bg-muted/40 px-2 py-0.5",
        "font-mono text-xs font-semibold tracking-[0.06em] text-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ── Money figure (tabular, Archivo) ────────────────────────────── */
export function Money({
  children,
  tone = "default",
  className,
}: {
  children: React.ReactNode;
  tone?: "default" | "pos" | "neg";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "font-num font-semibold tabular-nums",
        tone === "pos" && "text-success",
        tone === "neg" && "text-destructive",
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ── Mono text (timestamps, phones, codes) ──────────────────────── */
export function Mono({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("font-mono text-[12.5px]", className)}>{children}</span>
  );
}

/* ── Status chip with leading dot ───────────────────────────────── */
type ChipTone = "ok" | "warn" | "danger" | "muted";

const chipTones: Record<ChipTone, string> = {
  ok: "bg-success/10 text-success",
  warn: "bg-warning/10 text-warning",
  danger: "bg-destructive/10 text-destructive",
  muted: "bg-muted text-muted-foreground",
};

export function StatusChip({
  tone = "muted",
  dot = true,
  children,
  className,
}: {
  tone?: ChipTone;
  dot?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "status-chip inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold",
        chipTones[tone],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}
